import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next';
import { useMutation } from '@tanstack/react-query';
import { submitRelist } from '@services/my-orders-sales-service';
import useTimelineQuery from '@hooks/orders-sales/use-timeline-query';
import { TimelineRelistDetails } from '@interfaces/models/timeline';
import { Order } from '@interfaces/models/myOrder';
import { Product } from '@interfaces/models/orderSaleProduct';
import { onImageError, productImage } from '../helper';
import styles from '../popin.module.scss';
import { OkButtonView, OkView, PopinView } from './common';

const RelistView: React.FC<{
  closePopin: () => void;
  relistDetails: TimelineRelistDetails;
  product: Product;
  order: Order | null;
}> = ({ closePopin, relistDetails, product, order }) => {
  const { t } = useTranslation('orders-sales');
  const { refetchTimeline } = useTimelineQuery(product.productId, order ? order.orderId : null, false);

  const { productName, brandName, productId, imageUrl } = product;
  const { fee, publicPrice, sellerPrice } = relistDetails;

  const { isSuccess, isLoading, isError, mutate, data } = useMutation({
    mutationFn: (id: string) => submitRelist(id),
  });
  const hasError = isError || (data != null && data.kind !== 'Success');
  const hasSuccess = isSuccess && data != null && data.kind === 'Success';

  if (hasSuccess) {
    return (
      <PopinView
        closePopin={() => {
          refetchTimeline();
          closePopin();
        }}
        title={t('MY_ACCOUNT.ORDERS_SALES.POPUP.RELIST.TITLE')}
      >
        <OkView message={t('MY_ACCOUNT.ORDERS_SALES.POPUP.RELIST.SUCCESS_DESC')} />
      </PopinView>
    );
  }

  return (
    <PopinView
      closePopin={closePopin}
      title={t('MY_ACCOUNT.ORDERS_SALES.POPUP.RELIST.TITLE')}
    >
      <div className={clsx('vc-modal__content', styles.ordersSales__popin__content)}>
        <div className={styles.ordersSales__popin__product}>
          <div className={styles.ordersSales__popin__product__left}>
            <img
              className={styles.ordersSales__popin__product__left__image}
              src={productImage(imageUrl)}
              alt={productName}
              onError={onImageError}
            />
          </div>
          <div className={styles.ordersSales__popin__product__right}>
            <div className={styles.ordersSales__popin__product__right__name}>{productName}</div>
            <div className={styles.ordersSales__popin__product__right__type}>{brandName}</div>
            <div className={styles.ordersSales__popin__product__right__reference}>
              {t('MY_ACCOUNT.ORDERS_SALES.POPUP.RELIST.REFERENCE')} {productId}
            </div>
          </div>
        </div>
        <div className={styles.ordersSales__popin__relist}>
          <div className={styles.ordersSales__popin__relist__conditions}>
            {t('MY_ACCOUNT.ORDERS_SALES.POPUP.RELIST.CONDITIONS')}
          </div>
          <div className={styles.ordersSales__popin__relist__info}>
            <div className={styles.ordersSales__popin__relist__info__label}>
              {t('MY_ACCOUNT.ORDERS_SALES.POPUP.RELIST.PRICE')}
            </div>
            <div className={styles.ordersSales__popin__relist__info__value}>{publicPrice || '-'}</div>
          </div>
          <div className={styles.ordersSales__popin__relist__info}>
            <div className={styles.ordersSales__popin__relist__info__label}>
              {t('MY_ACCOUNT.ORDERS_SALES.POPUP.RELIST.FEE')}
            </div>
            <div className={styles.ordersSales__popin__relist__info__value}>{fee || '-'}</div>
          </div>
          <div className={styles.ordersSales__popin__relist__info}>
            <div className={styles.ordersSales__popin__relist__info__label}>
              {t('MY_ACCOUNT.ORDERS_SALES.POPUP.RELIST.FOR_YOU')}
            </div>
            <div className={styles.ordersSales__popin__relist__info__value}>{sellerPrice || '-'}</div>
          </div>
          <div className={styles.ordersSales__popin__relist__hint}>
            {t('MY_ACCOUNT.ORDERS_SALES.POPUP.RELIST.HINT')}
          </div>
        </div>
        {hasError && (
          <div className={styles.ordersSales__popin__errorText}>
            {t('MY_ACCOUNT.ORDERS_SALES.POPUP.RELIST.FAILURE_DESC')}
          </div>
        )}
        <div className={styles.ordersSales__popin__buttons}>
          <OkButtonView
            isSubmitting={isLoading}
            onClick={() => {
              mutate(productId);
            }}
          >
            {t('MY_ACCOUNT.ORDERS_SALES.ACCEPT')}
          </OkButtonView>
        </div>
      </div>
    </PopinView>
  );
};

export default RelistView;
